import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Button as MButton,
  IconButton,
  MenuItem,
  Popover,
  Select,
  TextField,
  ThemeProvider,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import brushLogo from 'assets/images/brush.svg';
import {
  customButtonTheme,
  customToolbarButtonTheme,
} from 'assets/styles/themes/generic_button_mui';
import {
  getCultureCampaignKey,
  getCultureCampaignKeyFromYear,
} from 'utils/Datagrid/CustomParcelstDataGridUtils';
import MapTab from './MapTab';
import { modes, requestFieldOffset } from './useMapParcelsModes';

const modeSwitchSx = {
  background: 'white',
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    padding: '2px 10px',
    fontSize: 13,
    color: '#277134',
    borderColor: '#277134',
  },
  '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover': {
    backgroundColor: '#277134',
    color: 'white',
  },
};

const brushButtonSx = {
  background: '#277134',
  color: 'white',
  height: 36,
  margin: 0,
  '&:hover': { background: '#277134' },
  '& .brush_logo': { filter: 'brightness(0) invert(1)' },
};

export const ModeSwitch = ({ mode, onChange, isGroupsDisabled }) => {
  const { t } = useTranslation();
  return (
    <ToggleButtonGroup exclusive size="small" value={mode} onChange={onChange} sx={modeSwitchSx}>
      <ToggleButton value={modes.cultures}>{t('map.mode.cultures')}</ToggleButton>
      <ToggleButton value={modes.groundTypes}>{t('map.mode.groundTypes')}</ToggleButton>
      <ToggleButton value={modes.groups} disabled={isGroupsDisabled}>
        {t('map.mode.groups')}
      </ToggleButton>
    </ToggleButtonGroup>
  );
};

export const BrushButton = ({ name, color, onFinish }) => {
  const { t } = useTranslation();
  return (
    <ThemeProvider theme={customToolbarButtonTheme}>
      <MButton sx={brushButtonSx} onClick={onFinish}>
        <span
          style={{
            width: 14,
            height: 14,
            borderRadius: 3,
            marginRight: 6,
            border: '1px solid rgba(0, 0, 0, 0.2)',
            background: `#${color}`,
          }}
        />
        {name} · {t('button.finish')}
        <img src={brushLogo} alt={t('alt.brushLogo')} className="brush_logo" />
      </MButton>
    </ThemeProvider>
  );
};

const getChangeLines = (request, forecastCampaign, t) =>
  Object.entries(request)
    .filter(([key]) => key !== 'id')
    .map(([key, value]) => {
      if (key === 'groundType') return `${t('map.groundType')} : ${value}`;
      return `${getCultureCampaignKey(requestFieldOffset(key), forecastCampaign)} : ${value}`;
    });

export const PendingChanges = ({ requests, parcelsData, forecastCampaign }) => {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState(null);
  return (
    <ThemeProvider theme={customButtonTheme}>
      <MButton size="small" onClick={(event) => setAnchor(event.currentTarget)}>
        {t('map.pendingChanges', { n: requests.length })}
      </MButton>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <div style={{ padding: 12, maxHeight: 320, overflowY: 'auto', minWidth: 240 }}>
          {requests.map((request) => (
            <div key={request.id} style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 600 }}>
                {parcelsData.find((parcel) => parcel.id === request.id)?.name}
              </div>
              {getChangeLines(request, forecastCampaign, t).map((line) => (
                <div key={line} style={{ fontSize: 13 }}>
                  {line}
                </div>
              ))}
            </div>
          ))}
        </div>
      </Popover>
    </ThemeProvider>
  );
};

const AddItemFooter = ({ options, selection, onAdd, label }) => {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 8px' }}>
      <Autocomplete
        size="small"
        fullWidth
        options={options}
        value={selection.itemToAdd}
        onChange={(_, value) => selection.setItemToAdd(value)}
        getOptionLabel={(option) => option?.name ?? ''}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => <TextField {...params} label={label} />}
      />
      <Tooltip title={t('common.add')}>
        <span>
          <IconButton
            size="small"
            onClick={onAdd}
            disabled={!selection.itemToAdd}
            aria-label={t('common.add')}
            sx={{ color: '#277134' }}
          >
            <AddIcon />
          </IconButton>
        </span>
      </Tooltip>
    </div>
  );
};

const YearSelector = ({ year, minYear, maxYear, setYear, forecastYear }) => {
  const { t } = useTranslation();
  const years = Array.from({ length: maxYear - minYear + 1 }, (_, index) => minYear + index);
  const getLabel = (value) =>
    value === forecastYear
      ? `${t('label.forecast')} ${value - 1}-${value}`
      : `${value - 1}-${value}`;
  return (
    <>
      <Select size="small" value={year} onChange={(event) => setYear(event.target.value)}>
        {years.map((value) => (
          <MenuItem key={value} value={value}>
            {getLabel(value)}
          </MenuItem>
        ))}
      </Select>
      <IconButton size="small" disabled={year <= minYear} onClick={() => setYear(year - 1)}>
        <ChevronLeftIcon />
      </IconButton>
      <IconButton size="small" disabled={year >= maxYear} onClick={() => setYear(year + 1)}>
        <ChevronRightIcon />
      </IconButton>
    </>
  );
};

export const MapItemsPanel = ({
  isCultureMode,
  forecastYear,
  forecastCampaign,
  pendingRequests,
  items,
  parcelsData,
  mapState,
  selection,
  isEditable,
  colorsByName,
}) => {
  const { t } = useTranslation();
  const campaignKey = getCultureCampaignKeyFromYear(mapState.year);

  const tableData = useMemo(() => {
    if (isCultureMode) return parcelsData;
    return parcelsData.map((parcel) => ({
      ...parcel,
      parcelCultures: parcel.groundType
        ? {
            [campaignKey]: {
              id: parcel.groundType.id,
              name: parcel.groundType.name,
              surface: parcel.surface,
            },
          }
        : {},
    }));
  }, [isCultureMode, parcelsData, campaignKey]);

  const usedIds = new Set(
    parcelsData.map((parcel) =>
      isCultureMode ? parcel.parcelCultures?.[campaignKey]?.id : parcel.groundType?.id,
    ),
  );
  const handleCultureHover = (name) => {
    if (!selection.selectedItem) mapState.handleCultureHover(name);
  };

  const addedYear = isCultureMode ? mapState.year : null;
  const addedForYear = useMemo(
    () => selection.addedItems.filter((item) => item.year === addedYear),
    [selection.addedItems, addedYear],
  );
  const availableItems = items.filter(
    (item) => !usedIds.has(item.id) && !addedForYear.some((addedItem) => addedItem.id === item.id),
  );

  const footer =
    isEditable && availableItems.length > 0 ? (
      <AddItemFooter
        options={availableItems}
        selection={selection}
        onAdd={() => selection.addItem(addedYear)}
        label={t(isCultureMode ? 'map.selectCulture' : 'map.selectGroundType')}
      />
    ) : null;

  return (
    <>
      {(isCultureMode || pendingRequests) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px' }}>
          {isCultureMode && (
            <YearSelector
              year={mapState.year}
              minYear={mapState.minYear}
              maxYear={mapState.maxYear}
              setYear={mapState.setYear}
              forecastYear={forecastYear}
            />
          )}
          {pendingRequests && (
            <div style={{ marginLeft: 'auto' }}>
              <PendingChanges
                requests={pendingRequests}
                parcelsData={parcelsData}
                forecastCampaign={forecastCampaign}
              />
            </div>
          )}
        </div>
      )}
      <MapTab
        data={tableData}
        header={['', t(isCultureMode ? 'common.culture' : 'map.groundType'), t('common.surface')]}
        isDynamic={true}
        year={mapState.year}
        minYear={mapState.minYear}
        maxYear={mapState.maxYear}
        setYear={mapState.setYear}
        parcelHoverCultureName={selection.selectedItem ? null : mapState.parcelHoverCultureName}
        parentCallback={handleCultureHover}
        fromParcels
        selectedCultureId={selection.selectedId}
        onSelectCulture={isEditable ? (id) => selection.select(id, addedYear) : null}
        addedItems={addedForYear}
        footer={footer}
        colorsByName={colorsByName}
        hideYears
        hideSubtotals={!isCultureMode}
      />
    </>
  );
};

const GroupsList = ({ groupMode }) => {
  const { t } = useTranslation();
  return (
    <>
      {groupMode.groups.map((group) => (
        <div
          key={group.parent.id}
          onClick={() => groupMode.setDraft(group)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
        >
          <span
            style={{
              width: 14,
              height: 14,
              borderRadius: 3,
              background: `#${groupMode.getGroupEntry(group.parent.id).color}`,
            }}
          />
          <span style={{ flex: 1 }}>{group.parent.name}</span>
          <span>{group.children.length + 1}</span>
        </div>
      ))}
      <MButton
        size="small"
        onClick={() => groupMode.setDraft({ parent: null, children: [] })}
        startIcon={<AddIcon />}
      >
        {t('map.groups.new')}
      </MButton>
    </>
  );
};

const GroupEditor = ({ groupMode }) => {
  const { t } = useTranslation();
  const { draft, isEdit, saving } = groupMode;
  return (
    <>
      <Autocomplete
        size="small"
        disabled={isEdit}
        options={isEdit ? [draft.parent] : groupMode.getParentOptions()}
        value={draft.parent}
        onChange={(_, parent) => groupMode.setDraft({ parent, children: [] })}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => <TextField {...params} label={t('label.mainParcel')} />}
      />
      <Autocomplete
        multiple
        disableCloseOnSelect
        size="small"
        disabled={!draft.parent}
        options={draft.parent ? groupMode.getEligibleChildren(draft.parent) : []}
        value={draft.children}
        onChange={(_, children) => groupMode.setDraft({ ...draft, children })}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => <TextField {...params} label={t('label.childParcels')} />}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <MButton size="small" onClick={groupMode.reset} disabled={saving}>
          {t('common.cancel')}
        </MButton>
        <MButton size="small" onClick={groupMode.save} disabled={!draft.children.length || saving}>
          {isEdit ? t('common.save') : t('button.validateGrouping')}
        </MButton>
      </div>
    </>
  );
};

export const MapGroupsPanel = ({ groupMode }) => (
  <ThemeProvider theme={customButtonTheme}>
    <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
      {groupMode.draft ? (
        <GroupEditor groupMode={groupMode} />
      ) : (
        <GroupsList groupMode={groupMode} />
      )}
    </div>
  </ThemeProvider>
);
